import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNotify, Title } from "react-admin";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Backdrop,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import { grey } from "@material-ui/core/colors";

import CrewCard from "../../../components/common/CrewCard";
import {
  InboundIcon,
  OutboundIcon,
  PlacedAtIcon,
  TruckIcon,
} from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import ShipsTogetherBadge from "../../../components/common/shipWith/ShipsTogetherBadge";
import {
  CarrierType,
  GateCheckInStatus,
  InboundStatus,
  OutboundStatus,
  UserType,
} from "../../../utils/constants";
import {
  GateDockField,
  GateSaveButton,
  GateStatusSelect,
} from "../../../managerApp/resources/gateCheckIns/GateCheckInFields";
import {
  GATE_ACTIVE_STATUSES,
  GateCheckInConflictError,
  fetchGateCheckInsByStatus,
  placedShipmentPath,
  saveGateCheckIn,
} from "../../../managerApp/resources/gateCheckIns/gateCheckInUtils";
import { useGateCheckInEditor } from "../../../managerApp/resources/gateCheckIns/useGateCheckInEditor";
import { fetchAllPages, logError } from "../../../utils/appUtils";
import { isAtLastStop, lastStop, stopHint } from "../../../utils/driverStops";
import { sessionYardDriverStops } from "../../../utils/gateAccess";
import { formatInYardTimeZone } from "../../../utils/dateUtils";
import {
  groupCheckInShipmentIds,
  isLinked,
  shipWithCacheKey,
} from "../../../utils/shipWith";
import {
  PlacementRefusal,
  gatePlacementRefusal,
  isAlreadyPlaced,
  placeShipment,
} from "../../../utils/shipmentPlacement";
import {
  canSetGateStatus,
  dedupeActiveCheckIns,
  gateStatusRefusal,
  gateStatusRestriction,
  isBoundByQueueOrder,
  nextInGateQueue,
  outOfTurnTooltip,
  sortByGateServiceTime,
} from "../../../utils/gateQueueOrder";
import { inboundShipmentByStatusGateQueue } from "../../../graphql/inbound/custom-queries";
import { outboundShipmentByStatusGateQueue } from "../../../graphql/outbound/custom-queries";
import useMounted from "../../../utils/useMounted";

// Same visibility-aware cadence as the manager Gate Queue page — a yard
// tablet that leaves this tab open all day should not poll a hidden page.
const REFRESH_INTERVAL = 20 * 1000;

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  topActions: {
    display: "flex",
    alignItems: "center",
  },
  avatar: {
    color: theme.palette.getContrastText(grey[300]),
    backgroundColor: grey[300],
  },
  rowActions: {
    // The actions ride as the row's last flex child rather than in a
    // ListItemSecondaryAction: a supervisor's row carries status, dock and save
    // beside Set Placed, which an absolutely positioned secondary action would
    // run off the side of a tablet. Wrapping keeps them all reachable.
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  // A linked truck's card heads with every PO on it and the badge that says
  // they ride together; wrapping keeps a long PO list readable on a tablet.
  groupPrimary: {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
}));

const DIRECTION = {
  INBOUND: "INBOUND",
  OUTBOUND: "OUTBOUND",
};

const { DOCKED, CALLED } = GateCheckInStatus;

/** Who runs the whole gate queue from the Operator App. A SUPERVISOR (and a
 * MANAGER, who also carries this app) keeps every control the manager Gate
 * Queue page has: any status and the dock assignment — calling drivers forward
 * included. CREW gets the one status change the yard floor needs — the truck is
 * at the dock — as a single button, so a crew member cannot call a driver
 * forward, complete a visit, or cancel a check-in from here. */
export const hasFullGateControl = (userType) =>
  userType === UserType.SUPERVISOR ||
  userType === UserType.MANAGER ||
  userType === UserType.ADMIN;

/** The active check-ins keyed by every shipment each one controls, so a queue
 * row can find the driver waiting on it. An inbound row names one load; an
 * outbound row names the load the driver searched and, on a linked truck
 * (LTL "ship with"), every load in `outboundIds` — each of which finds the
 * same driver here.
 *
 * A row naming none is not in the index and so reaches no shipment row —
 * there is no shipment to hang it on. It is still a truck at the gate, which
 * is why dedupeActiveCheckIns keeps it and why the queue order below is taken
 * from the check-ins themselves rather than from this index: the office would
 * otherwise call it forward from the manager Gate Queue while the yard floor
 * had never heard of it. Deduping again here is a no-op on an already-deduped
 * list and keeps this safe to call on a raw read.
 */
export const indexCheckInsByShipment = (checkIns) => {
  const byShipment = {};
  // Two active rows for one shipment can only mean a stale one; dedupe keeps
  // the newest. Shared with the manager Gate Queue, which lists the rows
  // themselves — the two pages must not disagree about which one is live.
  dedupeActiveCheckIns(checkIns).forEach((checkIn) => {
    const shipmentIds = checkIn.inboundId
      ? [checkIn.inboundId]
      : groupCheckInShipmentIds(checkIn);
    shipmentIds.forEach((shipmentId) => {
      byShipment[shipmentId] = checkIn;
    });
  });
  return byShipment;
};

// Only trucks move through the gate queue. Rail placement stays in Track
// Management, where placing a car also assigns its track location.
const truckFilter = { carrierType: { eq: CarrierType.TRUCK } };

/** When the truck checked in. Both directions are stamped by the driver check-in
 * (and by a manager entering the arrival by hand), each on the field its own
 * status derives from. No fallback to the appointment: the appointment is the
 * other half of the queue order and is shown on its own line, so folding it in
 * here would label a planned time as an arrival. */
const checkInStamp = (shipment) =>
  shipment.direction === DIRECTION.OUTBOUND
    ? shipment.arrivalDateTime
    : shipment.notifyDateTime;

const queueStamps = (shipment) => [
  shipment.scheduledDateTime,
  checkInStamp(shipment),
];

/** An appointment time is what gives a truck its place in the appointment
 * order, so a shipment without one is never advanced from the yard floor — a
 * manager schedules it (or places it from the gate check-in control) first. */
const hasAppointment = (shipment) => Boolean(shipment.scheduledDateTime);

/** Why this row cannot be placed, or null when it can. The crew cannot fix any
 * of these from this page, so the reason is what the tooltip shows. Ordered by
 * who has to act: the row's own missing data first, the appointment last.
 *
 * The inventory rule is inbound-only and applies to every truck: SIT is the one
 * exemption and SIT is rail, which never reaches the gate queue. Same gate the
 * manager's Inbound List advance button and dispatch form apply, so the two
 * surfaces cannot disagree about whether a shipment is placeable.
 *
 * The appointment is asked of a row no driver checked in against, and only of
 * that row. A truck with a check-in already holds a place in line by the time
 * it arrived (gateServiceTime falls back to the check-in stamp precisely so a
 * walk-in has one), and the office checks walk-ins in on purpose — through the
 * kiosk, or by hand from a dispatch tab, neither of which writes an
 * appointment. Requiring one of them would leave a truck a supervisor has
 * already called forward with a dead button and a tooltip telling the crew to
 * go find a manager, while the status select beside it placed the same
 * shipment on the same screen: stampPlacementFromGate, the DOCKED path, tests
 * the carrier fields and the inventory and never the appointment. A row with
 * no check-in keeps the rule, because the appointment is then the only stamp
 * that could give it a place in the queue at all.
 *
 * @param shipment the queue row
 * @param checkIn the driver waiting on it, or null when nobody checked in
 */
export const placementBlockedReason = (shipment, checkIn) => {
  const refusal = gatePlacementRefusal(
    shipment,
    shipment.direction === DIRECTION.OUTBOUND,
  );
  if (refusal) return refusal;
  if (!checkIn && !hasAppointment(shipment))
    return PlacementRefusal.NO_APPOINTMENT;
  return null;
};

/** The first refusal among a truck's loads, or null when every one can be
 * placed. A linked truck (LTL "ship with") is placed whole from one button,
 * so the button is held by whichever load is not ready and its tooltip names
 * that load's reason — the crew fix it, or find who can, before anything is
 * placed. A single load is its own one-member list. */
export const groupBlockedReason = (members, checkIn) => {
  for (const member of members) {
    const refusal = placementBlockedReason(member, checkIn);
    if (refusal) return refusal;
  }
  return null;
};

/** Everything this page requires of a card before its Set Placed button
 * works, independent of where the card sits in the queue. */
const canPlaceFromQueue = (members, checkIn) =>
  !groupBlockedReason(members, checkIn);

/**
 * The queue as cards, one per truck. A single load is its own card. Linked
 * outbound loads (LTL "ship with") that arrived together share one check-in
 * and one truck, so they fold into one card: `members` in queue order,
 * `shipment` the first of them (it names the card's place in line and its
 * carrier and trailer — one truck, one set), and the driver waiting on them.
 * Grouped by shipWithGroupId, with the sorted id list as the key for a row
 * that predates the group id. Rows are visited in queue order, so a card sits
 * where its earliest member does; members of one truck share an appointment
 * and an arrival stamp, so they are adjacent anyway.
 *
 * Only the loads this page lists are members: the queue is ARRIVED trucks,
 * and a sibling still PENDING (the office checked one load in by hand) is not
 * at the gate as far as this page can see. handlePlace tells the DOCKED save
 * whether the card covered every load the row names (coversEveryMember), so
 * the ones it did not are placed there instead.
 *
 * @returns {Array<{key: string, shipment: object, members: object[], checkIn: object|null}>}
 */
export const groupQueueRows = (shipments, checkInsByShipment) => {
  const cards = [];
  const byGroup = new Map();
  (shipments || []).forEach((shipment) => {
    const checkIn = checkInsByShipment[shipment.id] || null;
    const groupKey =
      shipment.direction === DIRECTION.OUTBOUND && isLinked(shipment)
        ? shipment.shipWithGroupId || shipWithCacheKey(shipment.shipWithIds)
        : null;
    if (!groupKey) {
      cards.push({ key: shipment.id, shipment, members: [shipment], checkIn });
      return;
    }
    let card = byGroup.get(groupKey);
    if (!card) {
      card = { key: `group:${groupKey}`, shipment, members: [], checkIn };
      byGroup.set(groupKey, card);
      cards.push(card);
    }
    card.members.push(shipment);
    if (!card.checkIn && checkIn) card.checkIn = checkIn;
  });
  return cards;
};

/** Whether a card's members are every load its check-in names. Set Placed
 * tells the DOCKED save the placement is done only when they are; otherwise
 * that save re-reads the row's loads and places the ones this page never
 * listed, skipping the ones placed here (stampPlacementFromGate no-ops on a
 * placed load). A row naming no loads, or no row at all, leaves nothing
 * uncovered. */
export const coversEveryMember = ({ members, checkIn }) =>
  groupCheckInShipmentIds(checkIn).every((id) =>
    members.some((member) => member.id === id),
  );

/** Whether a row offers Set Placed at all, whether it is live, and what the
 * tooltip says. Set Placed always means the same write — the check-in goes
 * DOCKED and the shipment goes PLACED with its placement stamp — so the rule
 * here is only about who may fire it, and when.
 *
 * A supervisor sees the button on every row and places in queue order: they call
 * the drivers forward, on the order the office published. A manager holds the
 * same controls unbound — the schedule is theirs, so placing out of turn is a
 * decision they are allowed to make (`orderBound` is what separates the two).
 *
 * Crew place only a driver a supervisor has already called, so queue order is
 * not what binds them — CALLED is, and where on the route the driver stands:
 * the button is live once the gate has sent them to the last stop (`stops`,
 * the yard's route — see src/utils/driverStops.js), and shown but held while
 * they are still at an earlier one, naming where they have to reach. A row
 * still sitting at ARRIVED, a row already DOCKED, and a row with no gate
 * check-in at all carry no button for crew: there is nobody at the gate they
 * were told to bring in.
 *
 * That last case is safe because this page only exists where the gate does.
 * A yard that checks trucks in at the office has no Gate Queue at all (see
 * src/utils/gateAccess.js) — its trucks are placed by a manager and reach the
 * crew through the inbound/outbound baskets. Everywhere this page renders,
 * every arrived truck carries a row: the driver's own, or the one the office
 * save writes for a truck that never reached the kiosk
 * (createManualGateCheckIn). No row here means that write failed, which the
 * manager was warned about and fixes by saving the shipment again.
 *
 * `members` are every load on the card — the one `shipment` for a single
 * load, all of them for a linked truck — and the first that cannot be placed
 * holds the button for all.
 *
 * @returns {{show: boolean, enabled?: boolean, tooltip?: string|null}}
 */
export const placeAction = ({
  shipment,
  members = [shipment],
  checkIn,
  fullControl,
  orderBound,
  isFrontOfQueue,
  frontLabel,
  isNextInQueue,
  nextLabel,
  stops,
}) => {
  const blockedReason = groupBlockedReason(members, checkIn);
  if (!fullControl) {
    if (checkIn?.status !== CALLED) return { show: false };
    if (!isAtLastStop(stops, checkIn.dockAssignment))
      return {
        show: true,
        enabled: false,
        tooltip: `Set Placed opens once the gate sends the driver to ${
          lastStop(stops)?.label
        }`,
      };
    return {
      show: true,
      enabled: !blockedReason,
      // The button moves the driver as well as the shipment, so say so — it is
      // the crew's only gate control, and its first half is otherwise invisible.
      tooltip:
        blockedReason || "Marks the driver at the dock and places the shipment",
    };
  }
  // Set Placed writes DOCKED, the very move the row's own status select
  // gates, so a row with a check-in answers to the same rule — otherwise the
  // button performs what the greyed-out dropdown beside it just refused. That
  // rule also frees a CALLED driver: they were let in in turn, and the crew's
  // button places them regardless of order, so the supervisor's must too. A
  // row with no check-in has no place in the check-in order, so the placement
  // order over the listed shipments holds it instead.
  const heldByQueue =
    orderBound &&
    (checkIn
      ? !canSetGateStatus({
          status: DOCKED,
          currentStatus: checkIn.status,
          orderBound,
          isNextInQueue,
        })
      : !isFrontOfQueue);
  return {
    show: true,
    enabled: !blockedReason && !heldByQueue,
    // The row's own missing data comes first: that is the reason this row was
    // passed over, and it says who has to fix it. Waiting on the queue is last —
    // it is the only reason that clears on its own, as the trucks ahead are
    // placed.
    tooltip: heldByQueue
      ? blockedReason || outOfTurnTooltip(checkIn ? nextLabel : frontLabel)
      : blockedReason,
  };
};

/** What Set Placed reports once the placement itself has succeeded, and whether
 * the page hands its reader on to the shipment.
 *
 * The placement and the DOCKED write cannot be one transaction, so this decides
 * what a half-done pair says. The shipment is placed in every branch — the
 * message names what is left undone rather than reporting a failure — and a
 * conflict is not one of those things: it means somebody moved the check-in
 * mid-place, so its status may already be right.
 *
 * Who is told to fix it depends on who is reading. Only full gate control
 * renders the status select beside the row, so pointing crew at gate controls
 * would name a button they do not have; their fix is the person at the gate,
 * whose own Gate Queue lists check-ins by status and so still holds this one
 * after the shipment leaves this page's queries.
 *
 * Navigation follows the same fact for the same reason it does on success:
 * placing takes the row off this page whether or not DOCKED was written, and
 * the load just spotted is the one the crew now work. Staying would leave them
 * on a queue that no longer lists it, holding a warning about a status they
 * cannot set. A supervisor or manager stays — calling drivers forward one after
 * another is what the queue is for.
 *
 * @param {{atDockError: Error|null, fullControl: boolean}} args
 * @returns {{message: string, severity: string|null, navigate: boolean}}
 */
export const placeOutcome = ({ atDockError, fullControl }) => {
  const navigate = !fullControl;
  if (!atDockError)
    return { message: "Shipment placed", severity: null, navigate };
  if (atDockError instanceof GateCheckInConflictError)
    return {
      message: fullControl
        ? "Shipment placed, but this check-in changed at the gate — check its status"
        : "Shipment placed, but this check-in changed at the gate",
      severity: "warning",
      navigate,
    };
  return {
    message: fullControl
      ? "Shipment placed, but the driver still shows Called — set Docked from the gate controls"
      : "Shipment placed, but the driver still shows Called — ask the gate to set them Docked",
    severity: "warning",
    navigate,
  };
};

/** The yard's active check-ins, deduped to one row per shipment (and every row
 * naming none). Fetched apart from the shipment queries and swallowing its own
 * failure: the trucks are what this page exists to show, so a gate-check-in
 * read that fails must cost the rows their gate controls, not the crew their
 * queue.
 *
 * Null, not an empty list, is what a failed read reports, so the caller can
 * keep the check-ins it already has. An empty list is an answer — every driver
 * is through the gate — and answering it on a transient AppSync error takes
 * Set Placed off every crew row until the next poll, twenty seconds of a
 * called driver standing at a dock nobody can spot. The shipment half of this
 * page already survives its own failure that way: its catch leaves the rows
 * alone. These reads are a poll apart from the truth at the gate in any case,
 * and the writes they lead to carry their own conditions.
 *
 * @returns {Promise<Array|null>} the deduped rows, or null when the read failed
 */
export const fetchActiveCheckIns = async (reloadYardId) => {
  try {
    return dedupeActiveCheckIns(
      await fetchGateCheckInsByStatus(reloadYardId, GATE_ACTIVE_STATUSES)
    );
  } catch (err) {
    logError("crewGateQueue:fetchCheckIns", err, false);
    return null;
  }
};

/** One queue row. The gate controls need their own editor per row, and an
 * editor is a hook — so a row is a component. The editor is the same one the
 * manager Gate Queue page runs, so a supervisor working the yard floor and a
 * manager in the office cannot drive the gate differently. */
const GateQueueRow = ({
  shipment,
  members = [shipment],
  checkIn,
  fullControl,
  orderBound,
  isNextInQueue,
  nextLabel,
  action,
  saving,
  onPlace,
  onChanged,
  secondaryText,
  timingText,
  stopText,
  classes,
}) => {
  const editor = useGateCheckInEditor(checkIn, {
    onSaved: onChanged,
    onReload: onChanged,
  });
  const showGateControls = fullControl && Boolean(checkIn);
  // Same rules the manager Gate Queue page applies to its status select: a
  // supervisor calls the truck at the front of the queue and nobody else, and
  // no one at all completes a visit the truck never docked for — nor one whose
  // loads, all of them on a linked truck, are still being worked.
  const isStatusAllowed = (status) =>
    !gateStatusRefusal({
      status,
      currentStatus: checkIn?.status,
      orderBound,
      isNextInQueue,
      shipments: members,
    });
  // A linked truck's card heads with every PO it carries and the badge that
  // says they ride together, so the crew know to open the rest once the first
  // is placed. A single load heads with its account, as it always has.
  const linked = members.length > 1;
  const poNumbers = members.map((member) => member.poNumber).filter(Boolean);
  const primary = linked ? (
    <span className={classes.groupPrimary}>
      <span>
        {poNumbers.length
          ? `PO #: ${poNumbers.join(", ")}`
          : shipment.accountName}
      </span>
      <ShipsTogetherBadge record={shipment} size="small" />
    </span>
  ) : (
    shipment.accountName
  );
  const restriction = gateStatusRestriction({
    currentStatus: checkIn?.status,
    orderBound,
    isNextInQueue,
    nextLabel,
  });
  const placeButton = action.show ? (
    <Button
      color="primary"
      size="small"
      startIcon={<PlacedAtIcon />}
      disabled={!action.enabled || saving}
      onClick={onPlace}
    >
      Set Placed
    </Button>
  ) : null;
  return (
    <ListItem alignItems="flex-start">
      <ListItemAvatar>
        <Avatar className={classes.avatar}>
          {shipment.direction === DIRECTION.OUTBOUND ? (
            <OutboundIcon fontSize="inherit" />
          ) : (
            <InboundIcon fontSize="inherit" />
          )}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={primary}
        secondary={
          <>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              component="span"
              display="block"
            >
              {secondaryText}
            </Typography>
            {timingText && (
              <Typography
                variant="subtitle2"
                color="textSecondary"
                component="span"
                display="block"
              >
                {timingText}
              </Typography>
            )}
            {stopText && (
              <Typography
                variant="subtitle2"
                color="textSecondary"
                component="span"
                display="block"
              >
                {stopText}
              </Typography>
            )}
          </>
        }
        primaryTypographyProps={{ display: "block", noWrap: false }}
        secondaryTypographyProps={{
          display: "block",
          noWrap: false,
          component: "span",
        }}
      />
      <div className={classes.rowActions}>
        {showGateControls && (
          <>
            <GateStatusSelect
              editor={editor}
              variant="table"
              isStatusAllowed={isStatusAllowed}
              restriction={restriction}
            />
            <GateDockField editor={editor} variant="table" />
            <GateSaveButton editor={editor} />
          </>
        )}
        {placeButton && action.tooltip ? (
          <Tooltip title={action.tooltip}>
            <span>{placeButton}</span>
          </Tooltip>
        ) : (
          placeButton
        )}
      </div>
    </ListItem>
  );
};

export const GateQueue = () => {
  const classes = useStyles();
  const notify = useNotify();
  const history = useHistory();
  const isComponentMounted = useMounted();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [shipments, setShipments] = useState([]);
  // The rows themselves, not an index by shipment: a check-in naming no
  // shipment is still a truck in the queue order, and the index has nowhere to
  // put it. The index is derived where the rows are rendered.
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  // Poll, visibility-return and the manual refresh button all call fetchQueue,
  // so several reads can be in flight at once. A monotonic request id (same
  // guard the Scheduler's availabilityRequestRef uses) records which read is the
  // latest; a slower earlier one that resolves after it is dropped rather than
  // overwriting the queue with staler rows.
  const fetchRequestRef = useRef(0);
  const fullControl = hasFullGateControl(window.appUser?.type);
  // The yard's stop route, read once per render: where a called driver is and
  // where they have to reach before the crew can place the load.
  const stops = sessionYardDriverStops();
  // A supervisor works the published order; a manager owns it. Crew never
  // advance a check-in from here at all, so the flag is moot for them.
  const orderBound = isBoundByQueueOrder(window.appUser?.type);

  const fetchQueue = useCallback(
    async (showSpinner = false) => {
      const reloadYardId = window.appUser?.reloadYardId;
      if (!reloadYardId) return;
      const requestId = fetchRequestRef.current + 1;
      fetchRequestRef.current = requestId;
      if (showSpinner) setLoading(true);
      // Kicked off alongside the shipment queries; it never rejects.
      const checkInsPromise = fetchActiveCheckIns(reloadYardId);
      try {
        // Both reads follow every page. truckFilter is a DynamoDB filter, which
        // is applied after the page is read rather than before: a yard whose
        // arrivals are mostly rail cars can fill a page with rows this queue
        // discards and leave its trucks on the next one, so a single-page read
        // can show an empty queue while trucks stand at the gate.
        const [inboundRows, outboundRows] = await Promise.all([
          fetchAllPages({
            query: inboundShipmentByStatusGateQueue,
            variables: {
              reloadYardId,
              statusReleaseDateTimePlacedDateTimeNotifyDateTimeScheduledDateTimeCreateDateTime:
                { beginsWith: { status: InboundStatus.ARRIVED } },
              filter: truckFilter,
            },
            responseKey: "inboundShipmentByReloadYardByStatusByDates",
            label: "crewGateQueue:inbound",
          }),
          fetchAllPages({
            query: outboundShipmentByStatusGateQueue,
            variables: {
              reloadYardId,
              status: { eq: OutboundStatus.ARRIVED },
              filter: truckFilter,
            },
            responseKey: "outboundShipmentByStatus",
            label: "crewGateQueue:outbound",
          }),
        ]);

        const inbounds = inboundRows.map((item) => ({
          ...item,
          direction: DIRECTION.INBOUND,
        }));
        const outbounds = outboundRows.map((item) => ({
          ...item,
          direction: DIRECTION.OUTBOUND,
        }));

        const data = sortByGateServiceTime(
          [...inbounds, ...outbounds],
          queueStamps
        );

        const activeCheckIns = await checkInsPromise;

        // Drop a read a newer one has already superseded, so a slow poll cannot
        // overwrite the fresher queue a later fetch just set.
        if (isComponentMounted.current && requestId === fetchRequestRef.current) {
          setShipments(data);
          // Null means that read failed; the check-ins already on screen are
          // the best answer there is until the next poll.
          if (activeCheckIns) setCheckIns(activeCheckIns);
        }
      } catch (err) {
        logError("crewGateQueue:fetchQueue", err, showSpinner ? notify : false);
      } finally {
        if (showSpinner && isComponentMounted.current) setLoading(false);
      }
    },
    [isComponentMounted, notify]
  );

  useEffect(() => {
    fetchQueue(true);
    const intervalId = setInterval(() => {
      if (document.visibilityState !== "hidden") fetchQueue();
    }, REFRESH_INTERVAL);
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchQueue();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchQueue]);

  const handlePlace = async (card) => {
    const { shipment, members, checkIn } = card;
    setSavingId(card.key);
    try {
      // Placement first, DOCKED second. The two writes cannot be one
      // transaction, so the order picks which half survives a failure: a
      // placed shipment whose driver still reads CALLED is a status a
      // supervisor can push from either Gate Queue page, while an DOCKED
      // check-in against an unplaced shipment points the driver at a dock no
      // crew list is working — and hides its own retry, since Set Placed
      // leaves a row that is no longer CALLED. The queue query carries every
      // composite sort-key member, so there is nothing to re-read before
      // placing — which is why the DOCKED save is told the placement is done
      // rather than left to re-read and place (saveGateCheckIn places before
      // it docks for every caller that does not say so).
      //
      // A linked truck's loads are placed together — one button, one truck —
      // and the DOCKED save is told the placement is done only when the card
      // covered every load the row names; otherwise it places the rest.
      //
      // One at a time, stopping at the first failure, and a member already
      // placed is skipped. Both matter on a retry: the card carries whole
      // shipment records read at the last poll, so re-placing a member that
      // went through on the previous attempt would write that stale snapshot
      // back over whatever the crew has done since, and append a second
      // placement note to its history. The catch refetches, so the retry is
      // offered from a card that shows what actually landed.
      for (const member of members) {
        const outbound = member.direction === DIRECTION.OUTBOUND;
        if (isAlreadyPlaced(member, outbound)) continue;
        await placeShipment(member, outbound);
      }
      let atDockError = null;
      if (checkIn && checkIn.status !== DOCKED) {
        try {
          await saveGateCheckIn(checkIn, {
            status: DOCKED,
            shipmentPlaced: coversEveryMember(card),
          });
        } catch (err) {
          atDockError = err;
          // A conflict is somebody else's edit landing first, not a fault to
          // log — placeOutcome says so to the reader instead.
          if (!(err instanceof GateCheckInConflictError))
            logError("crewGateQueue:handlePlace:atDock", err, false);
        }
      }
      const outcome = placeOutcome({ atDockError, fullControl });
      if (outcome.severity) notify(outcome.message, outcome.severity);
      else notify(outcome.message);
      if (outcome.navigate) {
        history.push(
          placedShipmentPath(
            shipment.id,
            shipment.direction === DIRECTION.OUTBOUND
          )
        );
        return;
      }
      await fetchQueue();
    } catch (err) {
      // Only placeShipment throws to here now — the DOCKED save handles its
      // own failures above, placement having already happened.
      logError("crewGateQueue:handlePlace", err, notify);
      // On a linked truck the members before the failure are placed. Refetch so
      // the card stops offering a retry built on records the last poll read.
      await fetchQueue();
    } finally {
      if (isComponentMounted.current) setSavingId(null);
    }
  };

  // A linked truck's card already heads with its POs, so its second line
  // carries the account and how many loads ride together instead; the carrier
  // and trailer are the truck's, so they read from the lead load either way.
  const getSecondaryText = ({ shipment, members }) => {
    const parts = [];
    const linked = members.length > 1;
    if (linked)
      parts.push(
        `${shipment.accountName} - ${members.length} loads on one truck`,
      );
    if (shipment.carrierName) parts.push(shipment.carrierName);
    if (shipment.railCarNumber) parts.push(`Trl #: ${shipment.railCarNumber}`);
    if (!isSmall && !linked && shipment.poNumber)
      parts.push(`PO #: ${shipment.poNumber}`);
    if (
      !isSmall &&
      !linked &&
      shipment.direction === DIRECTION.OUTBOUND &&
      shipment.pickupNumber
    )
      parts.push(`Pickup #: ${shipment.pickupNumber}`);
    return parts.join(" - ");
  };

  // Both halves of the queue order, so the crew can see why a truck sits where
  // it does: a 3pm appointment that checked in at 2:30 is called before a 4pm
  // appointment that checked in at 2:00.
  const getTimingText = (shipment) => {
    const parts = [];
    if (shipment.scheduledDateTime)
      parts.push(
        `Appt: ${formatInYardTimeZone(
          shipment.scheduledDateTime,
          "MMM d, h:mm a"
        )}`
      );
    const stamp = checkInStamp(shipment);
    if (stamp) {
      const label =
        shipment.direction === DIRECTION.OUTBOUND ? "Arrived" : "Notified";
      parts.push(`${label}: ${formatInYardTimeZone(stamp, "MMM d, h:mm a")}`);
    }
    return parts.join(" - ") || null;
  };

  // Which driver is waiting on which shipment. Derived rather than stored, so
  // the rows the index cannot key stay in the queue order computed below.
  const checkInsByShipment = indexCheckInsByShipment(checkIns);
  // One card per truck: a single load stands alone, linked loads that arrived
  // together fold into one card with one Set Placed button.
  const cards = groupQueueRows(shipments, checkInsByShipment);

  // The placement order over the listed trucks, for the cards no driver
  // checked in against — a card with a check-in answers to the check-in order
  // below instead. The front is the first card that can actually be placed,
  // not simply the first row: a card nobody can act on must not freeze the
  // trucks behind it (the queue mixes inbound and outbound, so that would stall
  // unrelated docks), so two kinds of card are skipped when looking for the
  // front:
  //   - no carrier name or trailer number: not fixable from this page;
  //   - no appointment time: it has no place in the appointment order, so it is
  //     scheduled first, or placed from a shipment's dispatch tab. Only a row
  //     with no check-in is asked for one — a truck that checked in takes its
  //     place from when it arrived.
  // The order binds the supervisor, who is the one calling drivers forward —
  // crew place only what they were called for, so CALLED binds them instead.
  // Off this page nothing consults the order: the manager's DOCKED control and
  // the shipment dispatch tabs both place directly.
  const front =
    cards.find((card) => canPlaceFromQueue(card.members, card.checkIn)) ||
    null;
  const frontLabel = (front && front.shipment.accountName) || "the shipment";

  // Whose turn it is at the gate, for the status select a supervisor carries on
  // every row. Ordered from the active check-ins themselves — the same rows,
  // stamps and order the manager Gate Queue page reads — so the yard office and
  // the yard floor cannot name different trucks next. In particular a check-in
  // whose shipment this page no longer lists (a manager placed it from its
  // dispatch tab while the driver waits) still holds the front, exactly as it
  // does on the manager page, and so does one naming no shipment at all: the
  // write refuses an out-of-turn call against this same list
  // (assertGateQueueTurn), so a row missing here would only grey the wrong
  // select and let the refusal come from the save.
  const nextCalled = orderBound
    ? nextInGateQueue(
        sortByGateServiceTime(checkIns, (checkIn) => [
          checkIn.scheduledDateTime,
          checkIn.arrivedAt,
        ]),
        (checkIn) => checkIn.status,
      )
    : null;
  const nextCalledLabel =
    nextCalled?.driverName || nextCalled?.carrierName || null;

  return (
    <CrewCard
      title="Gate Queue"
      icon={<TruckIcon fontSize="large" />}
      action={
        <div className={classes.topActions}>
          <IconButton onClick={() => fetchQueue(true)}>
            <RefreshIcon fontSize="large" />
          </IconButton>
        </div>
      }
    >
      <Title title={<ForceTitle title="Gate Queue" />} />
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid container>
        <Grid item xs={12}>
          <List>
            {cards.map((card, idx) => {
              const { shipment, members, checkIn } = card;
              const isNextInQueue =
                Boolean(nextCalled) &&
                Boolean(checkIn) &&
                nextCalled.id === checkIn.id;
              const action = placeAction({
                shipment,
                members,
                checkIn,
                fullControl,
                orderBound,
                isFrontOfQueue: Boolean(front) && card.key === front.key,
                frontLabel,
                isNextInQueue,
                nextLabel: nextCalledLabel,
                stops,
              });
              return (
                <React.Fragment key={card.key}>
                  <GateQueueRow
                    shipment={shipment}
                    members={members}
                    checkIn={checkIn}
                    fullControl={fullControl}
                    orderBound={orderBound}
                    isNextInQueue={isNextInQueue}
                    nextLabel={nextCalledLabel}
                    action={action}
                    saving={savingId === card.key}
                    onPlace={() => handlePlace(card)}
                    // The editor hands its saved row to the callback; the
                    // refetch takes a spinner flag, so it is not passed through.
                    onChanged={() => fetchQueue()}
                    secondaryText={getSecondaryText(card)}
                    timingText={getTimingText(shipment)}
                    // Where the driver is on the route, so a crew member
                    // looking at a held button can see what they wait for.
                    stopText={stopHint(stops, checkIn)}
                    classes={classes}
                  />
                  {idx < cards.length - 1 ? <Divider /> : null}
                </React.Fragment>
              );
            })}
            {!loading && shipments.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography color="textSecondary">
                      No arrived shipments waiting.
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
    </CrewCard>
  );
};
